/**
 * Newsletter Popup
 * Shows a subscribe popup based on configurable trigger conditions
 * and submits the email directly to Klaviyo's client-side subscribe API.
 *
 * Frequency rules (stored in localStorage / sessionStorage):
 *  - Once shown or closed → hidden for `reshowDays` days, then eligible again.
 *  - Once subscribed (form success, logged-in subscribed customer, or visitor
 *    arriving from a Klaviyo email link) → never shown again on this browser.
 *  - Never opens while the cart drawer / menu drawer / search overlay is open;
 *    waits until it closes.
 *
 * NC-CRO 2026-10:
 *  - Paid-social visits (fbclid, utm_source facebook/instagram/fb/ig/meta, or
 *    the Instagram/Facebook in-app browser; remembered in sessionStorage for
 *    the visit) open after `paidScrollPercentage`% scroll OR `paidDelaySeconds`,
 *    whichever comes first. Other traffic keeps the configured trigger.
 *  - Not shown to browsers Klaviyo already knows (klaviyo.isIdentified() or an
 *    identified __kla_id cookie) nor to logged-in customers with past orders.
 *  - Step 2 after signup: code with a one-tap "Copiar", then an optional
 *    "¿Cuál es tu talla?" step saved to the Klaviyo profile (shoe_size_eu) and
 *    to localStorage (nc_my_size). No auto-close any more.
 */

class NewsletterPopup {
  constructor() {
    this.popup = document.getElementById('NewsletterPopup');
    if (!this.popup) return;

    this.form = document.getElementById('NewsletterPopupForm');
    this.emailInput = document.getElementById('NewsletterPopupEmail');
    this.errorElement = document.getElementById('NewsletterPopupError');
    this.successView = document.getElementById('NewsletterPopupSuccessView');
    this.discountCopyBtn = document.getElementById('NewsletterPopupDiscountCopy');
    this.submitBtn = this.form ? this.form.querySelector('.newsletter-popup__submit') : null;
    // NC-CRO 2026-10: step 2 elements
    this.sizeStep = document.getElementById('NewsletterPopupSizeStep');
    this.continueBtn = document.getElementById('NewsletterPopupContinue');

    this.trigger = this.popup.dataset.trigger || 'delay';
    this.delaySeconds = parseFloat(this.popup.dataset.delaySeconds || '5');
    this.scrollPercentage = parseFloat(this.popup.dataset.scrollPercentage || '50');
    this.storageType = this.popup.dataset.storageType || 'local';
    this.reshowDays = parseFloat(this.popup.dataset.reshowDays || '14');
    this.customerSubscribed = this.popup.dataset.customerSubscribed === 'true';
    this.klaviyoCompanyId = this.popup.dataset.klaviyoCompanyId || '';
    this.klaviyoListId = this.popup.dataset.klaviyoListId || '';
    this.forceShow = this.popup.dataset.forceShow === 'true';
    // NC-CRO 2026-10
    this.customerHasOrders = this.popup.dataset.customerHasOrders === 'true';
    this.paidDelaySeconds = parseFloat(this.popup.dataset.paidDelaySeconds || '30');
    this.paidScrollPercentage = parseFloat(this.popup.dataset.paidScrollPercentage || this.popup.dataset.scrollPercentage || '50');
    // NC-CRO 2026-10: product pages are short on mobile (50 % ≈ right below the sizes),
    // so the paid-social scroll trigger waits until 75 % there.
    if (/\/products\//.test(window.location.pathname)) {
      this.paidScrollPercentage = Math.max(this.paidScrollPercentage, 75);
    }
    this.paidSocial = this.detectPaidSocial();
    this.subscribedEmail = '';

    this.legacyStorageKey = 'newsletter_popup_shown';
    this.storageKey = 'newsletter_popup_state';

    this.exitIntentTriggered = false;
    this.scrollTriggered = false;
    this.waitingForOverlay = false;

    this.setup();
  }

  setup() {
    if (!this.forceShow) {
      this.migrateLegacyFlag();

      if (this.customerSubscribed || this.cameFromKlaviyoEmail()) {
        this.writeState('subscribed');
        return;
      }

      // NC-CRO 2026-10: a first-order code is useless to logged-in past buyers.
      if (this.customerHasOrders) return;

      if (this.isSuppressed()) return;

      // NC-CRO 2026-10: an add-to-cart this visit = buying intent → no popup this session.
      if (this.addedToCartThisSession()) return;
      this.watchAddToCart();

      // NC-CRO 2026-10: Klaviyo already knows this browser → don't ask again.
      if (this.klaviyoCookieIdentified()) return;
      this.klaviyoKnown(); // warm up the async check before the trigger fires
    }

    this.setupCloseListeners();
    this.setupFormSubmit();
    this.setupTrigger();
  }

  /* ---------- storage ---------- */

  getStorage() {
    return this.storageType === 'session' ? sessionStorage : localStorage;
  }

  readState() {
    try {
      const raw = this.getStorage().getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  writeState(status) {
    if (this.forceShow) return;
    try {
      this.getStorage().setItem(this.storageKey, JSON.stringify({ status: status, ts: Date.now() }));
    } catch (error) {
      // Ignore storage errors (e.g. private browsing)
    }
  }

  // Old version stored a permanent "true" flag. Convert it to a dated
  // "dismissed" record so those visitors become eligible again after reshowDays.
  migrateLegacyFlag() {
    try {
      const storage = this.getStorage();
      if (storage.getItem(this.legacyStorageKey) === 'true') {
        if (!storage.getItem(this.storageKey)) this.writeState('dismissed');
        storage.removeItem(this.legacyStorageKey);
      }
    } catch (error) {
      // ignore
    }
  }

  isSuppressed() {
    const state = this.readState();
    if (!state) return false;
    if (state.status === 'subscribed') return true;

    // Session storage already resets per browser session; no day window needed.
    if (this.storageType === 'session') return true;

    const ageMs = Date.now() - (state.ts || 0);
    return ageMs < this.reshowDays * 24 * 60 * 60 * 1000;
  }

  cameFromKlaviyoEmail() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('_kx')) return true;
      const source = (params.get('utm_source') || '').toLowerCase();
      return source.indexOf('klaviyo') !== -1;
    } catch (error) {
      return false;
    }
  }

  /* ---------- NC-CRO 2026-10: audience checks ---------- */

  addedToCartThisSession() {
    try {
      return sessionStorage.getItem('nc_popup_atc') === '1';
    } catch (error) {
      return false;
    }
  }

  // Any /cart/add request (product form, quick add, cart upsells, apps) marks the session.
  watchAddToCart() {
    const mark = () => {
      this.atcThisSession = true;
      try {
        sessionStorage.setItem('nc_popup_atc', '1');
      } catch (error) {
        // ignore
      }
    };
    try {
      if ('PerformanceObserver' in window) {
        const po = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (/\/cart\/add(\.js)?(\?|$)/.test(entry.name)) mark();
          });
        });
        po.observe({ type: 'resource', buffered: true });
      }
    } catch (error) {
      // ignore
    }
    document.addEventListener(
      'submit',
      (e) => {
        const action = (e.target && e.target.getAttribute && e.target.getAttribute('action')) || '';
        if (/\/cart\/add/.test(action)) mark();
      },
      true
    );
  }

  // Paid social = fbclid / utm_source facebook|instagram|fb|ig|meta / IG-FB in-app
  // browser. Remembered for the visit (the URL params only exist on the landing page).
  detectPaidSocial() {
    try {
      if (sessionStorage.getItem('nc_paid_social') === '1') return true;
    } catch (error) {
      // ignore
    }
    let paid = false;
    try {
      const params = new URLSearchParams(window.location.search);
      const source = (params.get('utm_source') || '').toLowerCase();
      paid = params.has('fbclid') || /^(facebook|instagram|fb|ig|meta)([._-].*)?$/.test(source);
    } catch (error) {
      // ignore
    }
    if (!paid) paid = /Instagram|FBAN|FBAV|FB_IAB/i.test(navigator.userAgent || '');
    if (paid) {
      try {
        sessionStorage.setItem('nc_paid_social', '1');
      } catch (error) {
        // ignore
      }
    }
    return paid;
  }

  // klaviyo.js stores base64 JSON in __kla_id; it holds $exchange_id / $email
  // once the browser is identified (email click, form, checkout identify…).
  klaviyoCookieIdentified() {
    try {
      const match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      if (!match) return false;
      const data = JSON.parse(atob(decodeURIComponent(match[1])));
      return !!(data && (data.$exchange_id || data.$email));
    } catch (error) {
      return false;
    }
  }

  // Resolves true when klaviyo.isIdentified() says so; false on absence/timeout.
  klaviyoKnown() {
    if (this.klaviyoKnownPromise) return this.klaviyoKnownPromise;
    const kl = window.klaviyo;
    if (!kl || typeof kl.isIdentified !== 'function') return Promise.resolve(false); // retry on next call

    this.klaviyoKnownPromise = new Promise((resolve) => {
      let done = false;
      const finish = (value) => {
        if (done) return;
        done = true;
        resolve(value === true);
      };
      try {
        const result = kl.isIdentified(finish);
        if (result && typeof result.then === 'function') result.then(finish, () => finish(false));
      } catch (error) {
        finish(false);
      }
      setTimeout(() => finish(false), 2500);
    });
    return this.klaviyoKnownPromise;
  }

  track(name) {
    try {
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch (error) {
      // ignore
    }
  }

  /* ---------- triggers ---------- */

  setupTrigger() {
    if (this.forceShow) {
      this.openPopup();
      return;
    }

    // NC-CRO 2026-10: paid social → scroll depth OR time, whichever first.
    if (this.paidSocial) {
      this.setupPaidSocialTrigger();
      return;
    }

    switch (this.trigger) {
      case 'scroll':
        document.addEventListener('scroll', () => this.handleScroll(), { passive: true });
        break;
      case 'exit_intent':
        document.addEventListener('mouseout', (e) => this.handleExitIntent(e));
        break;
      case 'immediate':
        this.requestOpen();
        break;
      case 'delay':
      default:
        setTimeout(() => this.requestOpen(), this.delaySeconds * 1000);
        break;
    }
  }

  setupPaidSocialTrigger() {
    let fired = false;
    let timer = null;
    const onScroll = () => {
      if (this.scrolledPercent() >= this.paidScrollPercentage) fire();
    };
    const fire = () => {
      if (fired) return;
      fired = true;
      clearTimeout(timer);
      document.removeEventListener('scroll', onScroll);
      this.requestOpen();
    };
    document.addEventListener('scroll', onScroll, { passive: true });
    timer = setTimeout(fire, this.paidDelaySeconds * 1000);
  }

  scrolledPercent() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    return docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
  }

  handleScroll() {
    if (this.scrollTriggered) return;

    if (this.scrolledPercent() >= this.scrollPercentage) {
      this.scrollTriggered = true;
      this.requestOpen();
    }
  }

  handleExitIntent(event) {
    if (this.exitIntentTriggered) return;

    if (!event.relatedTarget && event.clientY <= 0) {
      this.exitIntentTriggered = true;
      this.requestOpen();
    }
  }

  /* ---------- overlay guard (cart drawer etc.) ---------- */

  isOtherOverlayOpen() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) return true;

    // NC-CRO 2026-10: back-in-stock modal (inline body overflow), size guide (<dialog>),
    // ATC bottom sheet, or any other native modal dialog.
    const bis = document.getElementById('BackInStockModal');
    if (bis && !bis.classList.contains('hidden')) return true;
    if (document.querySelector('dialog[open], atc-bottom-modal[open]')) return true;
    if (document.body.style.overflow === 'hidden') return true;

    const body = document.body.classList;
    return (
      body.contains('overflow-hidden') ||
      body.contains('overflow-hidden-mobile') ||
      body.contains('overflow-hidden-tablet') ||
      body.contains('overflow-hidden-desktop')
    );
  }

  // Open now, or wait until the cart drawer / other overlay is closed,
  // then open after a short pause so it doesn't feel like a reaction to closing it.
  requestOpen() {
    if (!this.isOtherOverlayOpen()) {
      this.openPopup();
      return;
    }
    if (this.waitingForOverlay) return;
    this.waitingForOverlay = true;

    const poll = setInterval(() => {
      if (this.isOtherOverlayOpen()) return;
      clearInterval(poll);
      setTimeout(() => {
        this.waitingForOverlay = false;
        if (this.isOtherOverlayOpen()) {
          this.requestOpen();
        } else {
          this.openPopup();
        }
      }, 3000);
    }, 1000);
  }

  /* ---------- open / close ---------- */

  setupCloseListeners() {
    const closeBtn = this.popup.querySelector('.newsletter-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePopup());
    }

    const overlay = this.popup.querySelector('.newsletter-popup__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.closePopup());
    }

    // NC-CRO 2026-10: a tap anywhere outside the card (the popup root fills the
    // screen) closes it too, even where the overlay is not the hit target.
    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup) this.closePopup();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.popup.classList.contains('hidden')) {
        this.closePopup();
      }
    });
  }

  setupFormSubmit() {
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }

    if (this.discountCopyBtn) {
      this.discountCopyBtn.addEventListener('click', () => this.handleCopyDiscountCode());
    }

    // NC-CRO 2026-10: step 2 controls
    if (this.continueBtn) {
      this.continueBtn.addEventListener('click', () => this.showSizeStep());
    }
    if (this.sizeStep) {
      this.sizeStep.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-nc-size]');
        if (chip) {
          this.handleSizeChoice(chip);
          return;
        }
        if (e.target.closest('[data-nc-size-skip]')) this.closePopup();
      });
    }
  }

  openPopup() {
    if (!this.popup.classList.contains('hidden')) return;
    if (this.forceShow) {
      this.showPopup();
      return;
    }
    if (this.atcThisSession || this.addedToCartThisSession()) return;
    // NC-CRO 2026-10: last check right before showing (identify may have happened meanwhile).
    if (this.klaviyoCookieIdentified()) return;
    this.klaviyoKnown().then((known) => {
      if (!known) this.showPopup();
    });
  }

  showPopup() {
    if (!this.popup.classList.contains('hidden')) return;

    this.popup.classList.remove('hidden');
    this.popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Counts as "seen" even if the visitor leaves without closing it.
    this.writeState('dismissed');
    this.track(this.paidSocial ? 'nc_popup_shown_paid' : 'nc_popup_shown');
  }

  closePopup() {
    this.popup.classList.add('hidden');
    this.popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    const state = this.readState();
    if (!state || state.status !== 'subscribed') this.writeState('dismissed');
  }

  /* ---------- form ---------- */

  async handleFormSubmit(event) {
    event.preventDefault();

    if (!this.emailInput.validity.valid) {
      this.showMessage(this.errorElement, this.emailInput.dataset.errorInvalid || 'Please enter a valid email address.');
      return;
    }

    const email = this.emailInput.value.trim().toLowerCase();

    this.setLoading(true);

    try {
      await this.subscribeToKlaviyo(email);
      this.writeState('subscribed');
      this.subscribedEmail = email;
      this.form.reset();
      this.showSuccessView();
      this.track('nc_popup_signup');
      // NC-CRO 2026-10: no auto-close — the visitor copies the code / picks a size.
    } catch (error) {
      console.error('Newsletter popup subscription error:', error);
      this.showMessage(this.errorElement, this.errorElement.dataset.errorGeneric || 'Something went wrong. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  showSuccessView() {
    this.errorElement.classList.add('hidden');
    this.form.classList.add('hidden');
    const heading = document.getElementById('NewsletterPopupHeading');
    if (heading) heading.classList.add('hidden');
    if (this.successView) this.successView.classList.remove('hidden');
  }

  async handleCopyDiscountCode() {
    const code = this.discountCopyBtn.dataset.code || '';
    if (!code) return;

    const copied = await this.copyText(code);
    if (!copied) return;

    const action = this.discountCopyBtn.querySelector('[data-nc-copy-label]');
    this.discountCopyBtn.classList.add('is-copied');
    if (action) action.textContent = action.dataset.copiedText || action.textContent;
    this.track('nc_popup_code_copied');
    // NC-CRO 2026-10: move on to the optional size question.
    setTimeout(() => this.showSizeStep(), 1200);
  }

  // NC-CRO 2026-10: navigator.clipboard is missing in some in-app browsers.
  async copyText(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      // fall through to the legacy path
    }
    try {
      const area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      area.remove();
      return ok;
    } catch (error) {
      console.error('Failed to copy discount code:', error);
      return false;
    }
  }

  /* ---------- NC-CRO 2026-10: step 2 — size ---------- */

  showSizeStep() {
    if (!this.sizeStep || !this.sizeStep.classList.contains('hidden')) return;
    if (this.popup.classList.contains('hidden')) return;
    if (this.continueBtn) this.continueBtn.classList.add('hidden');
    const intro = this.successView && this.successView.querySelector('.newsletter-popup__success-text');
    if (intro) intro.classList.add('hidden');
    this.popup.classList.add('is-step-size');
    this.sizeStep.classList.remove('hidden');
  }

  handleSizeChoice(chip) {
    const size = chip.dataset.ncSize;
    if (!size) return;
    this.sizeStep.querySelectorAll('[data-nc-size]').forEach((b) => {
      b.setAttribute('aria-pressed', b === chip ? 'true' : 'false');
      b.disabled = true;
    });
    this.saveSize(size);
    this.track('nc_popup_size_saved');

    const thanks = this.sizeStep.querySelector('[data-nc-size-thanks]');
    if (thanks) thanks.classList.remove('hidden');
    const skip = this.sizeStep.querySelector('[data-nc-size-skip]');
    if (skip) skip.classList.add('hidden');
    setTimeout(() => this.closePopup(), 1800);
  }

  saveSize(size) {
    try {
      localStorage.setItem('nc_my_size', size);
    } catch (error) {
      // ignore
    }
    try {
      document.dispatchEvent(new CustomEvent('nc:my-size', { detail: { size: size } }));
    } catch (error) {
      // ignore
    }

    const email = this.subscribedEmail;
    if (!email) return;
    const properties = { shoe_size_eu: size };

    this.updateKlaviyoProfile(email, properties).catch((error) => {
      // Fallback: klaviyo.js identify (also identifies this browser).
      try {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          window.klaviyo.identify(Object.assign({ email: email }, properties));
          return;
        }
      } catch (e) {
        // ignore
      }
      console.error('Newsletter popup size save error:', error);
    });
  }

  // Klaviyo client "create or update profile" (public key, no secret).
  async updateKlaviyoProfile(email, properties) {
    const url = `https://a.klaviyo.com/client/profiles/?company_id=${this.klaviyoCompanyId}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2026-04-15',
      },
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: {
            email: email,
            properties: properties,
          },
        },
      }),
    });
    if (!response.ok) throw new Error(`Klaviyo profile API returned ${response.status}`);
    return { success: true };
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;

    const text = this.submitBtn.querySelector('.newsletter-popup__submit-text');
    const spinner = this.submitBtn.querySelector('.newsletter-popup__submit-spinner');

    this.submitBtn.disabled = isLoading;
    if (text) text.classList.toggle('hidden', isLoading);
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  showMessage(element, message) {
    this.errorElement.classList.add('hidden');

    if (element) {
      element.textContent = message;
      element.classList.remove('hidden');
    }
  }

  async subscribeToKlaviyo(email) {
    const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoCompanyId}`;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.klaviyoListId,
            },
          },
        },
      },
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2026-04-15',
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 202) {
      return { success: true };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Klaviyo API Error:', errorData);
      throw new Error(`Klaviyo API returned ${response.status}`);
    }

    return { success: true };
  }
}

document.addEventListener('DOMContentLoaded', () => new NewsletterPopup());
